import { GUARD_RULES, LETTER_MAX_WORDS, type GuardRule } from "./customers.js";

/**
 * The letter a business sends somebody who has never heard of it
 * ([23](../../../docs/23-customers.md) §the letter, [24](../../../docs/24-everything-reach-does.md) §5).
 *
 * Overbill kept seven of these as files in its repo, each one written by hand in its own language with Overbill's
 * offer as fixed sentences. Here a letter has exactly three kinds of part, and only the middle one is a model's:
 *
 * 1. **The frame** — the greeting, the connective sentence and the stop line. Fixed per language, written by a
 *    person, never machine-translated, and the same for every business: they are the four sentences every cold
 *    letter in that language contains.
 * 2. **The opening line** — written for each company from the evidence sheet, and the only part a model writes.
 *    A sheet too thin to say something only this company could be told gives no opener, and so no letter.
 * 3. **The business's own words** — the offer, the ask, the signature and the legal footer. Written once by the
 *    owner on Set up, read from the mailbox and the business page, and never reworded by anybody.
 *
 * The guard below reads the finished thing, and reads it again before it goes.
 */

/* --------------------------------------------------------------------------------------- the frame --------- */

/** The fixed sentences of a letter, per language: a person wrote each one, and no machine translated it. */
export interface LetterFrame {
  language: string;
  /** What the language calls itself, for the screen. */
  name: string;
  /** "Hello," — a cold letter never opens with a first name it read off a website. */
  greeting: string;
  /**
   * "Hello Ahmed," — for a person somebody brought by name, on a list they imported (24 §1, 2026-10-06).
   *
   * The rule above stands: a first name read off a website is a guess about who opens the mail, and a guess at a
   * stranger's name is worse than no name. A name on a list a person brought is not a guess — they kept or bought
   * that list so that the letter reaches the one who decides, and *Hello,* to somebody whose name you have reads as
   * a letter sent to nobody in particular.
   */
  greets: (firstName: string) => string;
  /** How somebody stops hearing from this business, which every market here requires in every message. */
  stop: (email: string) => string;
  /** The word a reply must begin with to stop a sequence, lower-cased. */
  stopWord: string;
  /** What the footer says about why this address was written to: the company published it on its own site. */
  because: (company: string, email: string) => string;
  /**
   * The same sentence for an address a person brought on a list, naming where the list came from.
   *
   * The footer is the law's words and nobody edits it, so it has to be true of the address it is under. *Published
   * as a contact address for the company* is true of an address read off the company's own pages and false of one a
   * contact directory lists for a person who works there — which is why the source the person typed on the import is
   * kept on the company and said here, by name, in every letter that goes to such an address.
   */
  listed: (company: string, email: string, source: string) => string;
  /**
   * The two follow-ups, written by a person in this language and never machine-translated.
   *
   * Overbill's said something only Overbill could say. These say the two things any follow-up can honestly say:
   * the first repeats the **ask** without repeating the offer, because somebody who did not answer did not need
   * telling twice; the second offers to stop, because the kindest third message is the one that says it is the
   * last. Both are short, both carry the business's own ask, and neither adds a word of pressure.
   */
  followUps: ReadonlyArray<{ day: number; subject: (subject: string) => string; body: (p: FollowUpWords) => string }>;
  /** The words a reply begins with that mean *stop writing to me*, beyond the stop word itself. */
  optOutWords: readonly string[];
}

/** What a follow-up is written from: the business's own ask, and who signs it. */
export interface FollowUpWords extends SignedBy {
  company: string;
  ask: string;
  /** The person's first name, when the address is a named person's from a list (`greets`); otherwise nothing. */
  firstName?: string | null | undefined;
}

/**
 * Who a message is from, as a person signs one.
 *
 * Two lines, the way anybody's own mail ends: the person, then the business they write for and where to look it
 * up. The website is here because a stranger who is interested does the same thing next every time — they go and
 * see who this is — and a letter that makes them search for it has put a step in its own way. It is read from the
 * Business page, never typed on the letter.
 */
export interface SignedBy {
  signsAsName: string;
  signsAsRole: string;
  /** The business as its own page names it. */
  business: string;
  /** Its website, as the Business page holds it. Null when the page has none. */
  website: string | null;
}

/**
 * The sign-off: the person on one line, the business and its website on the next.
 *
 * No logo and no second address, because this is a plain-text letter
 * ([ADR 0046](../../../docs/adr/0046-a-cold-letter-is-plain-text-signs-off-with-the-business-and-never-repeats-itself.md)).
 */
export function signature(p: SignedBy): string {
  const person = [p.signsAsName, p.signsAsRole].filter((x) => x && x.trim()).join(", ").trim();
  const house = [p.business?.trim(), plainHost(p.website)].filter(Boolean).join(" — ");
  return [person, house].filter(Boolean).join("\n");
}

/** A website as somebody writes it at the bottom of an email: no scheme, no `www.`, no trailing slash. */
export function plainHost(url: string | null | undefined): string {
  const raw = (url ?? "").trim();
  if (!raw) return "";
  return raw.replace(/^[a-z][a-z0-9+.-]*:\/\//i, "").replace(/^www\./i, "").replace(/\/+$/, "").trim();
}

const signedBy = (p: FollowUpWords) => signature(p);

/** A follow-up's first words, with the person's first name in them when the letter it follows had one. */
const hello = (p: FollowUpWords, plain: string, named: (firstName: string) => string) =>
  p.firstName?.trim() ? named(p.firstName.trim()) : plain;

export const LETTER_FRAMES: readonly LetterFrame[] = [
  {
    language: "en", name: "English", greeting: "Hello,", stopWord: "stop",
    greets: (firstName) => `Hello ${firstName},`,
    because: (company, email) => `We are writing to ${email} because it is published as a contact address for ${company}, and only about your business.`,
    listed: (company, email, source) => `We are writing to ${email} because it is listed for ${company} in ${source}, a business contact directory, and only about your business.`,
    stop: () => `If you would rather not hear from us again, reply "stop" and we will not write again.`,
    optOutWords: ["stop", "unsubscribe", "remove me", "opt out", "opt-out", "take me off"],
    followUps: [
      {
        day: 4,
        subject: (subject) => subject,
        body: (p) => [
          hello(p, "Hello again,", (n) => `Hello again ${n},`),
          "",
          `A short one, in case my first message came at a busy moment.`,
          "",
          p.ask.trim(),
          "",
          signedBy(p),
        ].join("\n"),
      },
      {
        day: 11,
        subject: (subject) => subject,
        body: (p) => [
          hello(p, "Hello,", (n) => `Hello ${n},`),
          "",
          `I will not keep writing. Shall I close this for ${p.company}, or is it worth a look later in the year?`,
          "",
          signedBy(p),
        ].join("\n"),
      },
    ],
  },
  {
    language: "fr", name: "Français", greeting: "Bonjour,", stopWord: "stop",
    greets: (firstName) => `Bonjour ${firstName},`,
    because: (company, email) => `Nous écrivons à ${email}, adresse publiée comme contact de ${company}, uniquement au sujet de votre activité professionnelle.`,
    listed: (company, email, source) => `Nous écrivons à ${email}, adresse indiquée pour ${company} dans ${source}, un annuaire de contacts professionnels, uniquement au sujet de votre activité professionnelle.`,
    stop: () => `Vous pouvez vous y opposer à tout moment : répondez « stop » et nous ne vous écrirons plus.`,
    optOutWords: ["stop", "désabonner", "desabonner", "me désinscrire", "ne plus écrire", "ne plus recevoir"],
    followUps: [
      {
        day: 4,
        subject: (subject) => subject,
        body: (p) => [
          hello(p, "Rebonjour,", (n) => `Rebonjour ${n},`),
          "",
          "Un mot rapide, au cas où mon premier message serait tombé au mauvais moment.",
          "",
          p.ask.trim(),
          "",
          signedBy(p),
        ].join("\n"),
      },
      {
        day: 11,
        subject: (subject) => subject,
        body: (p) => [
          hello(p, "Bonjour,", (n) => `Bonjour ${n},`),
          "",
          `Je n'insiste pas davantage. Dois-je clore ce dossier pour ${p.company}, ou cela vaut-il un regard plus tard dans l'année ?`,
          "",
          signedBy(p),
        ].join("\n"),
      },
    ],
  },
  {
    language: "es", name: "Español", greeting: "Hola,", stopWord: "stop",
    greets: (firstName) => `Hola ${firstName},`,
    because: (company, email) => `Escribimos a ${email} porque figura como dirección de contacto de ${company}, y únicamente sobre su actividad profesional.`,
    listed: (company, email, source) => `Escribimos a ${email} porque figura como dirección de ${company} en ${source}, un directorio de contactos profesionales, y únicamente sobre su actividad profesional.`,
    stop: () => `Si prefiere no volver a recibir nuestros mensajes, responda «stop» y no le escribiremos más.`,
    optOutWords: ["stop", "baja", "darme de baja", "no escribir", "no quiero recibir"],
    followUps: [
      {
        day: 4,
        subject: (subject) => subject,
        body: (p) => [
          hello(p, "Hola de nuevo,", (n) => `Hola de nuevo, ${n},`),
          "",
          "Un mensaje breve, por si el primero llegó en mal momento.",
          "",
          p.ask.trim(),
          "",
          signedBy(p),
        ].join("\n"),
      },
      {
        day: 11,
        subject: (subject) => subject,
        body: (p) => [
          hello(p, "Hola,", (n) => `Hola ${n},`),
          "",
          `No insistiré más. ¿Cierro esto para ${p.company}, o merece una mirada más adelante?`,
          "",
          signedBy(p),
        ].join("\n"),
      },
    ],
  },
];

export function letterFrame(language: string): LetterFrame {
  return LETTER_FRAMES.find((f) => f.language === language) ?? LETTER_FRAMES[0]!;
}

/* ------------------------------------------------------------------------------------ the opening line ------ */

/**
 * The words that make a sentence a guess about the reader, per language.
 *
 * The opener's prompt forbids them and a model still writes them: the first letter on live, 2026-09-30, told a
 * baker "I read that as someone who bakes first and posts when there's time" — about a woman with forty food awards
 * and a recipe section. The reader is the one person who can tell it is a guess, so it is cut here by code rather
 * than left to a prompt.
 */
const GUESSES: Record<string, RegExp> = {
  en: /\b(usually|typically|probably|presumably|i imagine|i suspect|i guess|i'?d guess|i'?m guessing|i read (?:that|this|it) as|that tells me|which means|that kind of|it looks like you|it seems like you|sounds like you)\b/i,
  fr: /(\bgénéralement\b|\bprobablement\b|\bsans doute\b|\bj'imagine\b|\bje suppose\b|\bje devine\b|\bce qui veut dire\b|\bce qui signifie\b|\bj'y lis\b|\bon dirait que vous\b)/i,
  es: /(\bnormalmente\b|\bprobablemente\b|\bseguramente\b|\bme imagino\b|\bsupongo\b|\blo que significa\b|\blo interpreto como\b|\bparece que usted\b)/i,
};

/**
 * The opening line with every sentence that guesses about the reader taken out, or null when nothing is left.
 *
 * A sentence, not the whole line: a model's first sentence is usually what it read and its second what it made of
 * it, and the first alone is still a letter. Nothing left is no letter, the same rule as a model with nothing to say.
 */
export function withoutGuesses(opener: string, language: string): string | null {
  const guess = GUESSES[language] ?? GUESSES.en!;
  // A sentence ends at a stop followed by a space, so "co.uk" or "e.g." inside one does not split it.
  const sentences = opener.trim().split(/(?<=[.!?]["'”’)]*)\s+/);
  const kept = sentences.filter((s) => !guess.test(s)).join(" ").trim();
  return kept || null;
}

/* ------------------------------------------------------------------------------------ putting one together -- */

export interface LetterParts {
  language: string;
  /** The business writing, as its own page names it. */
  business: string;
  /** The company being written to. */
  company: string;
  toEmail: string;
  /** The one or two sentences written from the evidence sheet. */
  opener: string;
  /** The workspace's own offer and ask, in the owner's words. */
  offer: string;
  ask: string;
  /** From the mailbox: a real person who will answer. */
  signsAsName: string;
  signsAsRole: string;
  /** From the business page: where a stranger goes to look this business up. */
  website: string | null;
  /** From the business page: the company and its postal address. */
  footerCompany: string;
  footerAddress: string | null;
  /**
   * The person's first name, when the address is a named person's that somebody brought on a list: the letter greets
   * them by it. Nothing, and it says *Hello,* as it always has.
   */
  firstName?: string | null | undefined;
  /**
   * Where that list came from, as the person who imported it named it. Set, and the footer says the address is
   * listed there rather than published by the company (`LetterFrame.listed`).
   */
  listedIn?: string | null | undefined;
}

/**
 * The body of a letter, without the subject: everything between the greeting and the last line of the footer.
 *
 * There was a **bridge** here until 2026-09-21 — one fixed sentence, *"I am writing from Overbill."*, between the
 * opening line and the offer. It was written to carry the reader from the one to the other, and in a real letter
 * it did the opposite: the offer's own first words name the business, so the letter said the same thing twice in
 * two lines, which is exactly how a form letter reads. The offer names whoever is writing, because the owner wrote
 * it, and the signature names them again underneath. A third time was one too many.
 */
export function letterBody(p: LetterParts): string {
  const f = letterFrame(p.language);
  return [
    p.firstName?.trim() ? f.greets(p.firstName.trim()) : f.greeting,
    "",
    p.opener.trim(),
    "",
    p.offer.trim(),
    "",
    p.ask.trim(),
    "",
    signature(p),
  ].join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * The legal footer, kept apart from the body because nobody may edit it.
 *
 * It goes to strangers under the customer's own name, so it says who wrote, from where, why this address, and how
 * to stop — which is what every market this product writes in requires, in one shape. *Why this address* has two
 * honest answers and code chooses between them, never a person: the company published it on its own site, or a
 * person brought it on a list from a directory they named (`listed`), which is then said by name.
 */
export function letterFooter(p: LetterParts): string {
  const f = letterFrame(p.language);
  const who = [p.footerCompany, p.footerAddress].filter(Boolean).join(", ");
  const why = p.listedIn?.trim() ? f.listed(p.company, p.toEmail, p.listedIn.trim()) : f.because(p.company, p.toEmail);
  return [who ? `${who}.` : "", why, f.stop(p.toEmail)].filter(Boolean).join(" ");
}

/** The whole letter as it will be sent, for a screen to show and for the guard to read. */
export function wholeLetter(p: LetterParts): string {
  return `${letterBody(p)}\n\n---\n${letterFooter(p)}`;
}

/* --------------------------------------------------------------------------------------------- the guard --- */

export interface GuardInput {
  subject: string;
  /** The body and the footer, as they will be sent. */
  body: string;
  footer: string;
  /** The businesses the workspace's own page names under *Who else they could choose*. */
  rivals: readonly string[];
  /** Words this business never writes. Overbill's list holds one: *freight*. */
  neverWords: readonly string[];
  /** Every number the evidence sheet, the offer and the ask carry, as written. */
  allowedNumbers: readonly string[];
  /** The word a reply must begin with to stop, so the footer can be checked for it. */
  stopWord: string;
}

export interface GuardRefusal {
  rule: GuardRule;
  says: string;
  /** What in the letter tripped it, for the screen to show. Never the whole letter. */
  found: string;
}

/** A subject dressed up as a reply to a conversation that never happened. */
const FAKE_REPLY = /^\s*(re|fw|fwd|tr|rv|reenv|aw|wg)\s*[:：]/i;
/** Any number followed by a percent sign or the word for it, in the languages this product writes in. */
const PERCENT = /\d+(?:[.,]\d+)?\s*(?:%|per\s?cent|percent|pour\s?cent|por\s?ciento)/i;
/** A number worth checking: four or more digits, or a decimal, or money. Not "two" and not a year in prose. */
const NUMBER = /(?:[£$€]\s?\d[\d.,]*|\b\d[\d.,]{2,}\b)/g;
/**
 * A hole in a template that nobody filled: `{invite}`, `{company}`, `{first_name}`.
 *
 * Written on 2026-09-21 because one went out. Overbill's letters carried `{invite}`, its own free-look-back link,
 * which has no equivalent here; the importer strips the line, and twenty-eight letters drafted before it did kept
 * it. Nothing read the letter for it, so a rehearsal delivered a letter with `{invite}` in the middle of it. The
 * shape is deliberately narrow — a short word in braces — so that a price written as {1,2} or a brace somebody
 * meant is not mistaken for one.
 */
const PLACEHOLDER = /\{\s*[A-Za-z][A-Za-z0-9 _-]{0,28}\s*\}/;

/**
 * What a letter never says (Overbill's `guard.yaml`, made generic).
 *
 * Five of its six rules are the product's, because they are about honesty rather than about any one business: a
 * percentage, a rival, a number nothing backs, a subject pretending to be a reply, a missing stop line. The sixth
 * is the workspace's own list of words — Overbill's held *freight*, because its letters sold the parcel launch and
 * freight was a different conversation. Every business has one or two of those, and none of them is a line of this
 * product.
 *
 * A match is a **refusal with its reason**, never a silent rewrite: the person changes the sentence, and until they
 * do the letter does not go. It runs on the draft and again on what is approved, so an edit cannot walk past it.
 */
export function guardLetter(input: GuardInput): GuardRefusal[] {
  const refusals: GuardRefusal[] = [];
  const says = (rule: GuardRule) => GUARD_RULES.find((r) => r.code === rule)!.says;
  const text = `${input.subject}\n${input.body}`;
  const lower = text.toLowerCase();

  if (FAKE_REPLY.test(input.subject)) {
    refusals.push({ rule: "fake_reply", says: says("fake_reply"), found: input.subject.slice(0, 40) });
  }

  const percent = PERCENT.exec(text);
  if (percent) {
    // A percentage in a first letter is a claim about a company whose numbers nobody has seen.
    refusals.push({ rule: "unevidenced_number", says: "No percentage in a first letter: nobody has seen their numbers yet", found: percent[0] });
  }

  for (const rival of input.rivals) {
    const word = rival.trim().toLowerCase();
    if (word.length >= 4 && lower.includes(word)) {
      refusals.push({ rule: "names_a_rival", says: says("names_a_rival"), found: rival.trim() });
    }
  }

  for (const word of input.neverWords) {
    const w = word.trim().toLowerCase();
    if (w.length >= 2 && new RegExp(`(^|[^\\p{L}])${escape(w)}([^\\p{L}]|$)`, "iu").test(text)) {
      refusals.push({ rule: "forbidden_word", says: `This business never writes "${word.trim()}"`, found: word.trim() });
    }
  }

  const allowed = new Set(input.allowedNumbers.map(tidyNumber));
  for (const match of text.matchAll(NUMBER)) {
    const found = match[0];
    if (allowed.has(tidyNumber(found))) continue;
    refusals.push({ rule: "unevidenced_number", says: says("unevidenced_number"), found });
  }

  const hole = PLACEHOLDER.exec(text);
  if (hole) {
    refusals.push({ rule: "unfilled_placeholder", says: says("unfilled_placeholder"), found: hole[0].trim() });
  }

  // The body alone: the footer is the law's words and nobody may shorten them.
  const words = wordsIn(input.body);
  if (words > LETTER_MAX_WORDS) {
    refusals.push({
      rule: "too_long", says: says("too_long"), found: `${words} words, and ${LETTER_MAX_WORDS} is the most`,
    });
  }

  // The stop line, in the footer where it belongs. Every market this writes in requires one in every message.
  if (!input.footer.toLowerCase().includes(input.stopWord.toLowerCase())) {
    refusals.push({ rule: "no_stop_line", says: says("no_stop_line"), found: "" });
  }

  return dedupe(refusals);
}

/**
 * What the guard would refuse in the owner's own words, which go into every letter as written.
 *
 * The offer and the ask are never reworded (ADR 0046), so a rule they break stops every letter, and Set up said
 * nothing: Overbill's offer carried "30%", and each letter was held with *no percentage in a first letter* only when
 * somebody tried to send it (2026-09-29). The same rules, run where the words are typed. Length is the letter's, and
 * the stop line the footer's, so neither is judged here; numbers the owner writes are theirs to write.
 */
export function guardOwnWords(offer: string, ask: string, neverWords: readonly string[]): GuardRefusal[] {
  const stopWord = "stop";
  return guardLetter({
    subject: "", body: `${offer}\n${ask}`, footer: stopWord, rivals: [], neverWords,
    allowedNumbers: allowedNumbers(offer, ask), stopWord,
  }).filter((r) => r.rule !== "too_long" && r.rule !== "no_stop_line");
}

/** How many words a letter is, as a person would count them, for the guard and for the screen. */
export function wordsIn(text: string): number {
  return (text ?? "").trim().split(/\s+/).filter(Boolean).length;
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
/** A number as it compares: without its money sign, its spaces and its thousands separators. */
const tidyNumber = (s: string) => s.replace(/[£$€\s]/g, "").replace(/[.,](?=\d{3}\b)/g, "");

function dedupe(refusals: readonly GuardRefusal[]): GuardRefusal[] {
  const seen = new Set<string>();
  return refusals.filter((r) => {
    const key = `${r.rule}|${r.found.toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Every number a letter is allowed to contain: the ones the evidence sheet, the offer and the ask already carry.
 *
 * The rule it enforces is Overbill's — *never a figure not computed from that company's own files* — with the one
 * change the port needs: what backs a number here is the evidence read about that company plus the words the owner
 * wrote themselves, because the owner's own offer is allowed to say "one week" or "£50".
 */
export function allowedNumbers(...texts: readonly string[]): string[] {
  const found: string[] = [];
  for (const text of texts) for (const m of (text ?? "").matchAll(NUMBER)) found.push(m[0]);
  return found;
}

/* ------------------------------------------------------------------------------------------- the reply ---- */

/**
 * The address a reply to one letter comes back on (22 §replies).
 *
 * One per letter, and unguessable, because it is the whole mechanism: a reply arriving here is how a sequence
 * stops without Joogoos ever asking to read anybody's inbox. Reading a mailbox is a *restricted* Google scope and
 * costs a yearly audit by an outside firm; this costs a domain we already hold.
 */
export const replyAddress = (key: string, domain: string): string => `r-${key}@${domain}`;

/** The reply key out of an address, or null when it is not one of ours. */
export function replyKeyOf(address: string, domain: string): string | null {
  const m = new RegExp(`^r-([A-Za-z0-9_-]{6,64})@${domain.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`, "i")
    .exec(address.trim().toLowerCase());
  return m?.[1] ?? null;
}

/** What a reply turns out to be, once somebody has read the first line of it. */
export const REPLY_KINDS = ["reply", "opt_out", "bounce", "auto"] as const;
export type ReplyKind = (typeof REPLY_KINDS)[number];

/** Addresses and subjects that mean a machine answered, not a person. */
const AUTO_FROM = /^(mailer-daemon|postmaster|no-?reply|do-?not-?reply|bounce|bounces|automail)/i;
const AUTO_SUBJECT = /(out of (the )?office|automatic reply|réponse automatique|respuesta automática|absence du bureau|autoreply)/i;
const BOUNCE_SUBJECT = /(undelivered|undeliverable|delivery status notification|delivery has failed|mail delivery failed|returned mail|failure notice)/i;

/**
 * What a reply is, from its first line.
 *
 * Four answers and a strict order. A **bounce** first, because a delivery failure that looked like a reply would
 * stop a sequence *and* leave a dead address on the list. Then an **opt-out**, because somebody asking to be left
 * alone is owed that before anything else is read into what they wrote. Then an **automatic** answer, which stops
 * nothing — an out-of-office is not a no, and treating it as one loses a customer who was on holiday. Everything
 * else is a **reply**, and a person answers it.
 *
 * The opt-out is read from the first line only, the way Overbill read it: somebody who writes three paragraphs
 * ending "please stop" has been answered by a person long before a rule needs an opinion, and a rule that scans a
 * whole message finds "stop" in "we will stop using our current supplier".
 */
export function readReply(reply: { from: string; subject: string; text: string; language?: string }): ReplyKind {
  const local = (reply.from.split("@")[0] ?? "").trim();
  const subject = reply.subject ?? "";
  if (BOUNCE_SUBJECT.test(subject) || /^mailer-daemon/i.test(local)) return "bounce";

  const firstLine = (reply.text ?? "").split("\n").map((l) => l.trim()).find((l) => l.length > 0) ?? "";
  const said = firstLine.toLowerCase().replace(/[^\p{L}\p{N}\s-]/gu, " ").replace(/\s+/g, " ").trim();
  const frame = letterFrame(reply.language ?? "en");
  /**
   * Read from the first line only, and read two ways.
   *
   * A **short single word** — "stop", "baja" — has to open the line, because it is innocent in the middle of one:
   * "we are about to stop using our current supplier" is a customer, not somebody asking to be left alone. A
   * **longer word or a phrase** — "unsubscribe", "remove me", "no quiero recibir" — matches anywhere in that line,
   * because nobody writes those by accident, and people put "please" in front of them.
   *
   * The first line only, whichever it is: somebody who writes three paragraphs ending "please stop" has been read
   * by a person long before a rule needs an opinion.
   */
  const opening = said.split(" ").slice(0, 6).join(" ");
  const saysStop = frame.optOutWords.some((raw) => {
    const w = raw.toLowerCase();
    const plain = w.includes(" ") || w.length >= 8;
    return plain ? opening.includes(w) : opening === w || opening.startsWith(`${w} `);
  });
  if (saysStop) return "opt_out";

  if (AUTO_FROM.test(local) || AUTO_SUBJECT.test(subject)) return "auto";
  return "reply";
}

/** How long a letter waits after its last follow-up before it is closed as *no reply* (Overbill's §7: a week). */
export const NO_REPLY_AFTER_DAYS = 7;
